import { TableColumn, Badge, utils } from "@vilocnv/allsetra-core";
import { Box, IconButton, Typography } from "@mui/material";
import {
  SuccessReportIcon,
  FailedReportIcon,
  PendingReportIcon,
  InProgressReportIcon,
  DownloadXarReportIcon,
  DemoObjectCarIcon
} from "../../../../assets/icons";

export const XAR_REPORT_TABLE_COLUMNS = (
  translator: any,
  handleDownload: (row: any) => void
): TableColumn<any>[] => [
  {
    name: translator("tableHeading.triggerDate", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.processingStartedTimestampUtc
        ? utils.formatDate({
            date: row.processingStartedTimestampUtc,
            dateFormat: "dd-MMM-yyyy"
          })
        : "N/A"
    // wrap: true,
  },
  {
    name: translator("tableHeading.rangeStart", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.reportingStartDate
        ? utils.formatDate({
            date: row.reportingStartDate,
            dateFormat: "dd-MMM-yyyy"
          })
        : "N/A",
    wrap: true
  },
  {
    name: translator("tableHeading.rangeEnd", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) =>
      row?.reportingEndDate
        ? utils.formatDate({
            date: row.reportingEndDate,
            dateFormat: "dd-MMM-yyyy"
          })
        : "N/A",
    wrap: true
  },
  {
    name: translator("tableHeading.generation", {
      ns: "tableHeadingsTranslation"
    }),
    cell: (row: any) => {
      const getGenerationStatus = (status: string | number) => {
        switch (status) {
          case 0:
          case "in_progress":
            return {
              label: translator("reportStatus.inProgress"),
              color: "info",
              icon: InProgressReportIcon,
              textColor: "rgba(2, 105, 236, 1)"
            };
          case 1:
          case "completed":
            return {
              label: translator("reportStatus.completed"),
              color: "success",
              icon: SuccessReportIcon,
              textColor: "rgba(20, 142, 32, 1)"
            };
          case 2:
          case "failed":
            return {
              label: translator("reportStatus.failed"),
              color: "error",
              icon: FailedReportIcon,
              textColor: "rgba(204, 16, 16, 1)"
            };
          default:
            return {
              label: translator("reportStatus.unknown"),
              color: "default",
              icon: null
            };
        }
      };

      const status = getGenerationStatus(row?.status);
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "24px",
            "& > div": { padding: "0px !important" }
          }}
        >
          <Badge colorScheme={status.color as any}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "24px",
                px: "8px"
              }}
            >
              {status.icon && <status.icon />}
              <Typography
                sx={{ fontSize: "11px", fontWeight: 600, lineHeight: "13px" }}
                color={status.textColor}
              >
                {status.label}
              </Typography>
            </Box>
          </Badge>
        </Box>
      );
    }
  },
  {
    name: translator("tableHeading.validation", {
      ns: "tableHeadingsTranslation"
    }),
    cell: (row: any) => {
      const getValidationStatus = (status: string | number) => {
        switch (status) {
          case 0:
          case "notRun":
            return {
              label: translator("reportStatus.pending"),
              color: "warning",
              icon: PendingReportIcon,
              textColor: "rgba(226, 142, 16, 1)"
            };
          case 1:
          case "success":
            return {
              label: translator("reportStatus.valid"),
              color: "success",
              icon: SuccessReportIcon,
              textColor: "rgba(20, 142, 32, 1)"
            };
          case 2:
          case "warning":
            return {
              label: translator("reportStatus.warning"),
              color: "warning",
              icon: PendingReportIcon,
              textColor: "rgba(226, 142, 16, 1)"
            };
          case 3:
          case "error":
            return {
              label: translator("reportStatus.error"),
              color: "error",
              icon: FailedReportIcon,
              textColor: "rgba(204, 16, 16, 1)"
            };
          default:
            return {
              label: translator("reportStatus.unknown"),
              color: "default",
              icon: null
            };
        }
      };

      const status = getValidationStatus(row?.validationStatus);
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "24px",
            "& > div": { padding: "0px !important" }
          }}
        >
          <Badge colorScheme={status.color as any}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "24px",
                px: "8px"
              }}
            >
              {status.icon && <status.icon />}
              <Typography
                sx={{ fontSize: "11px", fontWeight: 600, lineHeight: "13px" }}
                color={status.textColor}
              >
                {status.label}
              </Typography>
            </Box>
          </Badge>
        </Box>
      );
    }
  },
  {
    name: translator("tableHeading.objects", {
      ns: "tableHeadingsTranslation"
    }),
    cell: (row: any) => {
      if (row.includeAllObjects === true) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "24px",
              "& > div": { padding: "0px !important" }
            }}
          >
            <Badge colorScheme={"info-blue"}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  px: "8px"
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    lineHeight: "13px",
                    color: "rgba(50, 57, 70, 1)"
                  }}
                >
                  {translator("reportStatus.allObjects")}
                </Typography>
              </Box>
            </Badge>
          </Box>
        );
      }

      if (row?.objectNames && Array.isArray(row.objectNames)) {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {row?.objectNames?.map((obj: any, index: number) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  "& > div": { padding: "0px !important" }
                }}
              >
                <Badge colorScheme={"info-blue"}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      height: "24px",
                      px: "8px"
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <DemoObjectCarIcon />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        lineHeight: "13px",
                        color: "rgba(50, 57, 70, 1)"
                      }}
                    >
                      {obj}
                    </Typography>
                  </Box>
                </Badge>
              </Box>
            ))}
          </Box>
        );
      }
      return "-";
    },
    wrap: true
  },
  {
    name: translator("tableHeading.driver", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => row?.driverName || "-",
    // selector: (row: any) => "N/A",
    wrap: true
  },
  {
    name: translator("tableHeading.fileSize", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => {
      if (row?.fileSizeKB) {
        if (typeof row.fileSizeKB === "number") {
          return row.fileSizeKB < 1024
            ? `${row.fileSizeKB}B`
            : `${Math.round(row.fileSizeKB / 1024)}kB`;
        }
        return row.fileSizeKB;
      }
      return "20kB"; // Default based on the design
    }
  },
  {
    name: "Download",
    cell: (row: any) =>
      (row.validationStatus === 1 || row.validationStatus === 2) &&
      row.status === 1 ? (
        <Box
          sx={{
            cursor: "pointer"
          }}
          onClick={() => {
            handleDownload(row);
          }}
        >
          <DownloadXarReportIcon />
        </Box>
      ) : (
        <Box
          sx={{
            cursor: "not-allowed",
            opacity: 0.5,
            filter: "grayscale(100%)"
          }}
        >
          <DownloadXarReportIcon />
        </Box>
      ),
    ignoreRowClick: true
    // allowOverflow: true,
    // button: true
  }
];
